import { useEffect, useMemo } from 'react';
import { connect } from 'react-redux';
import { Dispatch } from 'redux';
import { Button, ButtonLink, LoadingTable } from '../../../components/base';
import { ConfirmModal } from '../../../components/common/ConfirmModal';
import { DataTable, IDataTableColumn } from '../../../components/common/DataTable';
import { BlockCard, PageBody, PageContainer, PageHeader } from '../../../components/container';
import { BadgesRole } from '../../../components/shared/system/badges-role/BadgesRole';
import { BaseShowDetail } from '../../../context/base/BaseShowDetail';
import { eBaseActionStatus } from '../../../context/base/eBaseActionStatus';
import { RoleResponse } from '../../../context/models/roles/RoleModel';
import { UserGroupModel } from '../../../context/models/users-group/UserGroupModel';
import { actions } from '../../../context/stores/rootActions';
import { AppState } from '../../../context/stores/rootReducers';
import { UserGroupActionTypes } from '../../../context/stores/users-group/IUserGroupActionTypes';
import UsersGroupDetailModal from './detail-modal';

type IUsersGroupPageProps = {
	titlePage: string;
	status: eBaseActionStatus;
	users_group_all: UserGroupModel[];
	allRoles: RoleResponse[];
	selectedIds: number[];
	showDetail: BaseShowDetail<UserGroupModel>;
	showConfirm: boolean;
	handleReloadAllData: () => void;
	handleSaveData: (data: UserGroupModel) => void;
	handleDeleteData: (data: number[]) => void;
	onChangeSelectedIds: (ids: number[]) => void;
	onShowDetail: (data: BaseShowDetail<UserGroupModel>) => void;
	onShowConfirm: (isShow: boolean) => void;
};

const mapStateToProps = (state: AppState) => {
	return {
		titlePage: `Tác nhân`,
		status: state.usersGroup.status,
		users_group_all: state.usersGroup.users_group_all,
		allRoles: state.roles.allRoles,
		selectedIds: state.usersGroup.selectedIds,
		showDetail: state.usersGroup.showDetail,
		showConfirm: state.usersGroup.showConfirm,
	};
};

const mapDispatchToProps = (dispatch: Dispatch<UserGroupActionTypes>) => ({
	handleReloadAllData: () => {
		dispatch(actions.usersGroup.getAllRequest());
	},
	handleSaveData: (data: UserGroupModel) => {
		dispatch(actions.usersGroup.saveRequest(data));
	},
	handleDeleteData: (ids: number[]) => {
		dispatch(actions.usersGroup.deleteRequest(ids));
	},
	onChangeSelectedIds: (ids: number[]) => {
		dispatch(actions.usersGroup.changeSelectedIds(ids));
	},
	onShowDetail: (data: BaseShowDetail<UserGroupModel>) => {
		dispatch(actions.usersGroup.showDetail(data));
	},
	onShowConfirm: (isShow: boolean) => {
		dispatch(actions.usersGroup.showConfirm(isShow));
	},
});

const UsersGroupPage = (props: IUsersGroupPageProps) => {
	const {
		titlePage,
		status,
		selectedIds,
		showConfirm,
		showDetail,
		users_group_all,
		allRoles,
		handleReloadAllData,
		onChangeSelectedIds,
		handleDeleteData,
		handleSaveData,
		onShowDetail,
		onShowConfirm,
	} = props;

	useEffect(() => {
		handleReloadAllData();
		//eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		if (status === eBaseActionStatus.reload) handleReloadAllData();
		//eslint-disable-next-line react-hooks/exhaustive-deps
	}, [status]);

	const calculateFilterExpression = (filterValue: any, selectedFilterOperation: any, target: any) => {
		if (target === 'search' && typeof filterValue === 'string') {
			return [target.dataField, 'contains', filterValue];
		}
		return function (data: any) {
			const list = (data.roleids ? data.roleids : '0').split(',').map((x: any) => parseInt(x));
			return list.indexOf(filterValue) !== -1;
		};
	};

	const columns = useMemo((): IDataTableColumn<UserGroupModel>[] => {
		return [
			{ accessorKey: 'name', header: 'Tên', size: 150 },
			{ accessorKey: 'description', header: 'Mô tả', minSize: 150 },
			{
				accessorKey: 'roleids',
				header: 'Quyền hạn',
				size: 250,
				cell: ({ getValue }) => {
					const roleIds = (getValue() as string)?.split(',').map((x: any) => parseInt(x)) || [];
					return <BadgesRole roles_all={allRoles} value={roleIds} />;
				}
			},
			{
				accessorKey: 'isActive',
				header: 'Áp dụng',
				size: 100,
				cell: ({ getValue }) => {
					const val = getValue() as boolean;
					return <span className={val ? "text-primary/90 font-medium" : "text-muted-foreground"}>{val ? 'Áp dụng' : 'Không'}</span>;
				}
			},
			{
				id: 'actions',
				header: 'Sửa',
				meta: { align: 'center' },
				size: 60,
				cell: ({ row }) => (
					<ButtonLink onClick={() => onShowDetail({ isShow: true, detailData: row.original })} title='Sửa' icon='ni ni-edit' theme='link' />
				)
			}
		];
		//eslint-disable-next-line react-hooks/exhaustive-deps
	}, [allRoles]);

	return (
		<>
			<PageContainer>
				<PageHeader title={`Danh sách ${titlePage}`}>
					<div className='flex flex-wrap items-center justify-end'>
						<ul className='flex flex-wrap items-center gap-2 [&>li]:list-none'>
							<li>
								<Button
									text={`Xóa (${selectedIds.length})`}
									icon='icon ni ni-trash'
									className='hidden md:inline-flex'
									theme='danger'
									isDisabled={selectedIds.length <= 0}
									onClick={() => onShowConfirm(true)}
								/>
							</li>

							<li>
								<Button
									text='Tạo mới'
									icon='icon ni ni-plus'
									theme='primary'
									className='hidden md:inline-flex'
									onClick={() => onShowDetail({ isShow: true })}
								/>
							</li>
						</ul>
					</div>
				</PageHeader>
				<PageBody>
					<BlockCard>
						{status === eBaseActionStatus.loading && <LoadingTable />}
						{status !== eBaseActionStatus.loading && (
							<DataTable
								data={users_group_all}
								columns={columns}
								isLoading={status !== eBaseActionStatus.complete}
								enableRowSelection
								rowSelection={selectedIds.reduce((acc, id) => ({ ...acc, [String(id)]: true }), {})}
								onRowSelectionChange={(selection) => {
									onChangeSelectedIds(Object.keys(selection).filter(k => selection[k]).map(Number));
								}}
								enablePagination
								totalRecords={users_group_all.length}
								enableGlobalFilter
								toolbar={{
									isShowSearch: true,
									searchPlaceholder: `Tìm kiếm...`,
									extraActions: <span className='text-sm text-muted-foreground'>Tổng số: <b>{users_group_all.length}</b></span>
								}}
								onRefresh={handleReloadAllData}
							/>
						)}
					</BlockCard>
				</PageBody>
			</PageContainer>
			{showDetail && showDetail.isShow && (
				<>
					<UsersGroupDetailModal
						isSaving={status === eBaseActionStatus.saving}
						isShow={showDetail.isShow}
						detailModel={showDetail.detailData}
						onClose={() => onShowDetail({ isShow: false })}
						onSubmit={(data) => handleSaveData(data)}
						title={`${showDetail.detailData ? `Cập nhật` : `Thêm mới`} ${titlePage}`}
					/>
				</>
			)}
			{showConfirm && (
				<>
					<ConfirmModal
						show={showConfirm}
						innerText={`Bạn chắc chắn muốn xóa ${selectedIds.length} ${titlePage.toLowerCase()} đã chọn ?`}
						type='danger'
						onClose={() => onShowConfirm(false)}
						onConfirmed={() => handleDeleteData(selectedIds)}
						isLoading={status === eBaseActionStatus.loading || status === eBaseActionStatus.saving}
						btnConfirmText='Xóa'
						btnCloseText='Không xóa'
					/>
				</>
			)}
		</>
	);
};

export default connect(mapStateToProps, mapDispatchToProps)(UsersGroupPage);